import {
	type OutboundPayload,
	type OutboundSchemaFieldDefinition,
	parsePhoneNumberValue,
} from "@repo/common";
import * as XLSX from "xlsx";

export interface BatchParseRowError {
	rowIndex?: number;
	fieldName?: string;
	columnName?: string;
	message: string;
}

export interface BatchParseResult {
	valid: boolean;
	totalRows: number;
	errors: BatchParseRowError[];
	taskInputs: OutboundPayload[];
	mappedColumns: Record<string, string>;
}

export async function parseSpreadsheetFile(
	file: File,
): Promise<{ headers: string[]; rows: Record<string, unknown>[] }> {
	const buffer = await file.arrayBuffer();
	const workbook = XLSX.read(buffer, { type: "array" });
	if (workbook.SheetNames.length === 0) {
		return { headers: [], rows: [] };
	}
	const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
	const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
		firstSheet,
		{
			defval: "",
			raw: true,
		},
	);

	if (rawRows.length === 0) {
		return { headers: [], rows: [] };
	}

	const headers = Object.keys(rawRows[0] || {});
	return { headers, rows: rawRows };
}

function normalizeHeader(value: string): string {
	return value.trim().toLowerCase();
}

function isPhoneFieldName(name: string): boolean {
	const lower = name.trim().toLowerCase();
	return (
		lower.includes("phone") ||
		lower.includes("mobile") ||
		lower.includes("cell") ||
		lower === "to" ||
		lower === "from"
	);
}

function parseBooleanValue(value: unknown): boolean | null {
	if (typeof value === "boolean") return value;
	if (typeof value === "number") return value !== 0;
	if (typeof value === "string") {
		const trimmed = value.trim().toLowerCase();
		if (["true", "1", "yes", "y"].includes(trimmed)) return true;
		if (["false", "0", "no", "n"].includes(trimmed)) return false;
	}
	return null;
}

function parseDateValue(value: unknown): string | null {
	if (typeof value !== "string" && typeof value !== "number") return null;
	const strVal = String(value).trim();
	if (/^\d{4}-\d{2}-\d{2}$/.test(strVal)) return strVal;
	const parsed = new Date(strVal);
	if (!Number.isNaN(parsed.getTime())) {
		return parsed.toISOString().slice(0, 10);
	}
	return null;
}

function parseDateTimeValue(value: unknown): string | null {
	if (typeof value !== "string" && typeof value !== "number") return null;
	const strVal = String(value).trim();
	const parsed = new Date(strVal);
	if (!Number.isNaN(parsed.getTime())) {
		return parsed.toISOString();
	}
	return null;
}

export function validateAndMapBatchRows(
	schemaFields: OutboundSchemaFieldDefinition[],
	headers: string[],
	rows: Record<string, unknown>[],
): BatchParseResult {
	const errors: BatchParseRowError[] = [];
	const mappedColumns: Record<string, string> = {};
	const normalizedHeaders = headers.map(normalizeHeader);

	if (rows.length === 0) {
		errors.push({ message: "Uploaded file contains no data rows." });
		return {
			valid: false,
			totalRows: 0,
			errors,
			taskInputs: [],
			mappedColumns,
		};
	}

	if (rows.length > 450) {
		errors.push({
			message: `File contains ${rows.length} rows, which exceeds the maximum limit of 450 rows per run batch.`,
		});
	}

	// Step 1: Map fields to columns
	for (const field of schemaFields) {
		const targetColumnName = field.columnName?.trim() || field.name.trim();
		const normalizedTarget = normalizeHeader(targetColumnName);
		const normalizedFieldName = normalizeHeader(field.name);

		const headerIndex = normalizedHeaders.findIndex(
			(h) => h === normalizedTarget || h === normalizedFieldName,
		);

		if (headerIndex !== -1) {
			mappedColumns[field.name] = headers[headerIndex];
		} else if (field.required) {
			errors.push({
				fieldName: field.name,
				columnName: targetColumnName,
				message: `Missing required column for field "${field.name}" (expected column header "${targetColumnName}").`,
			});
		}
	}

	// Step 2: Validate each row
	const taskInputs: OutboundPayload[] = [];

	for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
		const rawRow = rows[rowIndex];
		const rowNumber = rowIndex + 1;
		const taskInput: Record<string, unknown> = {};

		for (const field of schemaFields) {
			const matchedHeader = mappedColumns[field.name];
			const rawValue = matchedHeader ? rawRow[matchedHeader] : undefined;
			const isValueEmpty =
				rawValue === undefined ||
				rawValue === null ||
				(typeof rawValue === "string" && rawValue.trim() === "");

			if (field.required && isValueEmpty) {
				errors.push({
					rowIndex: rowNumber,
					fieldName: field.name,
					columnName: matchedHeader || field.columnName || field.name,
					message: `Row ${rowNumber}: Missing required value for field "${field.name}".`,
				});
				continue;
			}

			if (isValueEmpty) {
				continue;
			}

			if (field.type === "number") {
				const num = Number(rawValue);
				if (Number.isNaN(num) || !Number.isFinite(num)) {
					errors.push({
						rowIndex: rowNumber,
						fieldName: field.name,
						columnName: matchedHeader,
						message: `Row ${rowNumber}: Value "${String(rawValue)}" for field "${field.name}" must be a number.`,
					});
				} else {
					taskInput[field.name] = num;
				}
			} else if (field.type === "boolean") {
				const boolVal = parseBooleanValue(rawValue);
				if (boolVal === null) {
					errors.push({
						rowIndex: rowNumber,
						fieldName: field.name,
						columnName: matchedHeader,
						message: `Row ${rowNumber}: Value "${String(rawValue)}" for field "${field.name}" must be a boolean (true/false, 1/0, yes/no).`,
					});
				} else {
					taskInput[field.name] = boolVal;
				}
			} else if (field.type === "date") {
				const dateStr = parseDateValue(rawValue);
				if (!dateStr) {
					errors.push({
						rowIndex: rowNumber,
						fieldName: field.name,
						columnName: matchedHeader,
						message: `Row ${rowNumber}: Value "${String(rawValue)}" for field "${field.name}" must be a valid date (YYYY-MM-DD).`,
					});
				} else {
					taskInput[field.name] = dateStr;
				}
			} else if (field.type === "datetime") {
				const dateTimeStr = parseDateTimeValue(rawValue);
				if (!dateTimeStr) {
					errors.push({
						rowIndex: rowNumber,
						fieldName: field.name,
						columnName: matchedHeader,
						message: `Row ${rowNumber}: Value "${String(rawValue)}" for field "${field.name}" must be a valid date-time.`,
					});
				} else {
					taskInput[field.name] = dateTimeStr;
				}
			} else if (
				field.type === "phone_number" ||
				isPhoneFieldName(field.name)
			) {
				const phoneStr = parsePhoneNumberValue(rawValue);
				if (!phoneStr) {
					if (field.type === "phone_number") {
						errors.push({
							rowIndex: rowNumber,
							fieldName: field.name,
							columnName: matchedHeader,
							message: `Row ${rowNumber}: Value "${String(rawValue)}" for field "${field.name}" must be a valid phone number.`,
						});
					} else {
						taskInput[field.name] = String(rawValue).trim();
					}
				} else {
					taskInput[field.name] = phoneStr;
				}
			} else {
				// string type
				let strVal = String(rawValue).trim();
				const parsedPhone = parsePhoneNumberValue(rawValue);
				if (parsedPhone) {
					strVal = parsedPhone;
				}
				if (
					field.enumValues &&
					field.enumValues.length > 0 &&
					!field.enumValues.includes(strVal)
				) {
					errors.push({
						rowIndex: rowNumber,
						fieldName: field.name,
						columnName: matchedHeader,
						message: `Row ${rowNumber}: Value "${strVal}" for field "${field.name}" is not allowed. Options: ${field.enumValues.join(", ")}.`,
					});
				} else {
					taskInput[field.name] = strVal;
				}
			}
		}

		taskInputs.push(taskInput as OutboundPayload);
	}

	return {
		valid: errors.length === 0,
		totalRows: rows.length,
		errors,
		taskInputs,
		mappedColumns,
	};
}
